import { useEffect, useState, useMemo, useRef } from "react";
import { useStore } from "../state/useStore";
import { useDesktopStore } from "../state/desktopStore";
import { SessionCard } from "../components/SessionCard";
import { useToast } from "../components/Toast";
import { Select } from "../components/Select";
const POLL_INTERVAL = 30_000; // Auto-refresh every 30s

export function Home() {
  const { sessions, isLoading, fetchSessions, uploadFile } = useStore();
  const { isTauri, isWatching } = useDesktopStore();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "track" | "car">("date");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const filtered = useMemo(() => {
    let result = [...sessions];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        (s.trackName || "").toLowerCase().includes(q) ||
        (s.carName || "").toLowerCase().includes(q) ||
        (s.eventType || "").toLowerCase().includes(q)
      );
    }
    if (sortBy === "track") {
      result.sort((a, b) => (a.trackName || "").localeCompare(b.trackName || ""));
    } else if (sortBy === "car") {
      result.sort((a, b) => (a.carName || "").localeCompare(b.carName || ""));
    }
    // default "date" keeps API order (newest first)
    return result;
  }, [sessions, search, sortBy]);

  // Initial fetch + auto-refresh polling (OT-097)
  useEffect(() => {
    document.title = "Sessions | SimSense";
    fetchSessions();

    pollRef.current = setInterval(() => {
      if (document.hasFocus()) fetchSessions();
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchSessions]);

  const handleUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".ibt";
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        try {
          await uploadFile(file);
          toast.success("File uploaded successfully! Processing in background.");
        } catch (err: any) {
          toast.error("Upload failed: " + err.message);
        }
      }
    };
    input.click();
  };

  // Performance Hero stats
  const totalSessions = sessions.length;
  const totalLaps = sessions.reduce((sum, s) => sum + (s.lapCount || 0), 0);
  const uniqueTracks = new Set(sessions.map(s => s.trackName).filter(Boolean)).size;

  // Time on track — estimate from best lap time * lap count
  // bestLapTime is in seconds (from API), guard against outliers < 10s
  const totalTimeSec = sessions.reduce((sum, s) => {
    const best = s.bestLapTime;
    if (!best || best < 30) return sum; // skip invalid/outlier values (30s floor per OT-232)
    const estAvg = best * 1.08; // avg lap ~8% slower than best
    return sum + (estAvg * (s.lapCount || 0));
  }, 0);
  const totalHours = totalTimeSec / 3600;
  const timeOnTrack = totalHours >= 1 ? `${totalHours.toFixed(1)}h` : totalTimeSec > 0 ? `${Math.round(totalHours * 60)}m` : "—";

  // Most driven car
  const carCounts: Record<string, number> = {};
  sessions.forEach(s => { if (s.carName) carCounts[s.carName] = (carCounts[s.carName] || 0) + 1; });
  const topCar = Object.entries(carCounts).sort((a, b) => b[1] - a[1])[0];
  const mostDrivenCar = topCar ? topCar[0].replace(/\s*\d{4}$/, "").replace(/^.+\s-\s/, "") : "—";

  return (
    <div>
      {/* Performance Hero */}
      {sessions.length > 0 && (
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "16px", marginBottom: "28px",
        }}>
          {[
            { label: "Sessions", value: totalSessions, accent: false },
            { label: "Total Laps", value: totalLaps.toLocaleString(), accent: false },
            { label: "Time on Track", value: timeOnTrack, accent: true },
            { label: "Tracks Driven", value: uniqueTracks, accent: false },
            { label: "Most Driven", value: mostDrivenCar, accent: false, small: mostDrivenCar.length > 12 },
          ].map(stat => (
            <div key={stat.label} style={{
              padding: "20px", background: "var(--card)", borderRadius: "var(--radius)",
              border: "1px solid var(--border)", textAlign: "center",
            }}>
              <div style={{ fontSize: "11px", color: "var(--text3)", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.5px", marginBottom: "6px" }}>{stat.label}</div>
              <div style={{ fontSize: (stat as any).small ? "15px" : "24px", fontWeight: 700, color: stat.accent ? "var(--accent)" : "var(--text1)", fontVariantNumeric: "tabular-nums" }}>{stat.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h2>Recent Sessions</h2>
        
        <div style={{ display: "flex", gap: "12px" }}>
          {!isWatching && (
            <button
              onClick={handleUpload}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                background: "var(--card)",
                border: "1px solid var(--border)",
                color: "var(--text1)",
                borderRadius: "var(--radius-sm)",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all var(--transition)",
              }}
            >
              ↑ Manual Upload
            </button>
          )}
          {isWatching && (
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: "8px 16px", 
              background: "rgba(255,255,255,0.02)", 
              border: "1px dashed var(--border)",
              borderRadius: "var(--radius-sm)",
              fontSize: "12px",
              color: "var(--text2)",
              fontWeight: 500
            }}>
              <span style={{ color: "var(--good)" }}>●</span> Auto-Importing
            </div>
          )}
        </div>
      </div>

      {/* Search & Filter Bar */}
      {sessions.length > 0 && (
        <div style={{
          display: "flex",
          gap: "8px",
          marginBottom: "16px",
          alignItems: "center",
        }}>
          <input
            type="text"
            placeholder="Search track, car, or event type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: "8px 12px",
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              color: "var(--text)",
              fontSize: "13px",
              outline: "none",
            }}
          />
          <Select
            value={sortBy}
            onChange={v => setSortBy(v as "date" | "track" | "car")}
            options={[
              { value: "date", label: "Sort: Date" },
              { value: "track", label: "Sort: Track" },
              { value: "car", label: "Sort: Car" },
            ]}
            size="sm"
            style={{ minWidth: "130px" }}
          />
          {search && (
            <div style={{ fontSize: "12px", color: "var(--text2)", whiteSpace: "nowrap" }}>
              {filtered.length} of {sessions.length}
            </div>
          )}
        </div>
      )}

      {isLoading && sessions.length === 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="skeleton skeleton-card" style={{ padding: "16px" }}>
              <div className="skeleton skeleton-text" style={{ width: "40%", marginBottom: "12px" }} />
              <div className="skeleton skeleton-title" />
              <div className="skeleton skeleton-text" style={{ width: "55%" }} />
            </div>
          ))}
        </div>
      ) : sessions.length === 0 ? (
        /* Empty state */
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 24px",
            background: "var(--card)",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏁</div>
          <h3 style={{ marginBottom: "8px" }}>No sessions yet</h3>
          <p
            style={{
              color: "var(--text2)",
              textAlign: "center",
              maxWidth: "400px",
              marginBottom: "24px"
            }}
          >
            {isTauri 
              ? "The agent is waiting for your first iRacing session." 
              : "Upload an iRacing .ibt file or download the Desktop Agent to automate your workflow."}
          </p>
          {!isTauri && (
            <button 
              onClick={() => {
                window.location.href = "https://releases.simsense.app/telemetry-agent/SimSense_Telemetry_Agent_0.2.0_x64-setup.exe";
              }}
              style={{
                padding: "10px 20px",
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--radius-sm)",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Download Desktop Agent
            </button>
          )}
        </div>
      ) : filtered.length === 0 && search ? (
        <div style={{
          padding: "40px",
          textAlign: "center",
          background: "var(--card)",
          borderRadius: "var(--radius)",
          border: "1px solid var(--border)",
        }}>
          <div style={{ fontSize: "13px", color: "var(--text2)" }}>
            No sessions match "{search}"
          </div>
          <button
            onClick={() => setSearch("")}
            style={{
              marginTop: "12px",
              padding: "6px 16px",
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--text)",
              borderRadius: "var(--radius-sm)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            Clear search
          </button>
        </div>
      ) : (
        /* Session List */
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "16px",
          }}
        >
          {filtered.map((session) => (
            <SessionCard key={session.id} session={session} />
          ))}
        </div>
      )}

      {/* Quick Stats + Recent Activity */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr",
          gap: "16px",
          marginTop: "16px",
        }}
      >
        <div
          style={{
            padding: "16px",
            background: "var(--card)",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
          }}
        >
          <h3
            style={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--text2)",
              marginBottom: "12px",
            }}
          >
            Quick Stats
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <StatRow label="Sessions" value={sessions.length.toString()} />
            <StatRow label="This week" value={sessions.filter(s => {
              const d = new Date(s.sessionDate);
              const now = new Date();
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return d >= weekAgo;
            }).length.toString()} />
            <StatRow label="Tracks driven" value={new Set(sessions.map(s => s.trackName).filter(Boolean)).size.toString()} />
          </div>
        </div>

        {sessions.length > 0 && (
          <div
            style={{
              padding: "24px",
              background: "var(--card)",
              borderRadius: "var(--radius)",
              border: "1px solid var(--border)",
            }}
          >
            <h3
              style={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "var(--text2)",
                marginBottom: "16px",
              }}
            >
              Recent Activity
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <StatRow
                label="Most driven car"
                value={(() => {
                  const cars = sessions.map(s => s.carName).filter(Boolean);
                  if (!cars.length) return "—";
                  const counts: Record<string, number> = {};
                  cars.forEach(c => { counts[c] = (counts[c] || 0) + 1; });
                  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
                })()}
              />
              <StatRow
                label="Most driven track"
                value={(() => {
                  const tracks = sessions.map(s => s.trackName).filter(Boolean);
                  if (!tracks.length) return "—";
                  const counts: Record<string, number> = {};
                  tracks.forEach(t => { counts[t] = (counts[t] || 0) + 1; });
                  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
                })()}
              />
              <StatRow
                label="Latest session"
                value={sessions[0]?.trackName || "—"}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "13px",
      }}
    >
      <span style={{ color: "var(--text2)" }}>{label}</span>
      <span style={{ fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </span>
    </div>
  );
}
